import { useEffect, useState } from "react";
import API, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import AddressAutocomplete from "./AddressAutocomplete";
import { toast } from "sonner";

const EMPTY = { name: "", address_line: "", town_city: "", county: "", postcode: "", what3words: "", property_type: "", site_contact_name: "", site_contact_phone: "", site_contact_email: "", access_notes: "" };

/** Admin dialog: add or edit a property in a client's portfolio. onSaved(property) after success.
 *  `accounts` ([{id, label, group?}]) adds an account picker so the office can add straight to any client / sub-client. */
export default function ClientPropertyDialog({ clientId, property, open, onClose, onSaved, accounts }) {
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [account, setAccount] = useState(clientId || "");
  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));

  useEffect(() => {
    if (open) { setF(property ? { ...EMPTY, ...property } : EMPTY); setAccount(clientId || ""); }
  }, [open, property, clientId]);

  const targetId = accounts ? account : clientId;
  const groups = accounts ? accounts.reduce((m, a) => { (m[a.group || ""] = m[a.group || ""] || []).push(a); return m; }, {}) : null;

  const save = async () => {
    if (!f.name.trim() || !f.address_line.trim() || !f.postcode.trim()) return toast.error("Name, address and postcode are required");
    if (!targetId) return toast.error("Pick the client or sub-client this property belongs to");
    setSaving(true);
    try {
      const { data } = property?.id
        ? await API.put(`/clients/${targetId}/properties/${property.id}`, f)
        : await API.post(`/clients/${targetId}/properties`, f);
      const acc = accounts?.find((a) => a.id === targetId);
      toast.success(property?.id ? "Property updated" : acc ? `Property added to ${acc.label}` : "Property added to the client's portfolio");
      onSaved && onSaved(data, targetId);
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not save property");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="client-property-dialog">
        <DialogHeader><DialogTitle>{property?.id ? "Edit property" : "Add property to portfolio"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {accounts && !property?.id && (
            <div>
              <Label className="text-xs">Belongs to *</Label>
              <select value={account} onChange={(e) => setAccount(e.target.value)} className="mt-1 h-9 w-full rounded-md border border-input bg-background px-3 text-sm" data-testid="client-property-account">
                <option value="">Choose a client or sub-client…</option>
                {Object.entries(groups).map(([g, list]) => g ? (
                  <optgroup key={g} label={g}>{list.map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}</optgroup>
                ) : list.map((a) => <option key={a.id} value={a.id}>{a.label}</option>))}
              </select>
            </div>
          )}
          <AddressAutocomplete label="Search address" placeholder="Start typing postcode or address…" className="mb-1"
            onSelect={(a) => setF(prev => ({ ...prev, address_line: a.street_address || prev.address_line, town_city: a.town_city || prev.town_city, county: a.county || prev.county, postcode: a.postcode || prev.postcode, name: prev.name || a.street_address || "" }))}
            data-testid="client-property-autocomplete" />
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label className="text-xs">Property name *</Label><Input value={f.name} onChange={e => set("name", e.target.value)} placeholder="Head Office, Block B" className="mt-1" data-testid="client-property-name" /></div>
            <div className="col-span-2"><Label className="text-xs">Address line *</Label><Input value={f.address_line} onChange={e => set("address_line", e.target.value)} className="mt-1" data-testid="client-property-address" /></div>
            <div><Label className="text-xs">Town / City</Label><Input value={f.town_city} onChange={e => set("town_city", e.target.value)} className="mt-1" data-testid="client-property-town" /></div>
            <div><Label className="text-xs">County</Label><Input value={f.county} onChange={e => set("county", e.target.value)} className="mt-1" data-testid="client-property-county" /></div>
            <div><Label className="text-xs">Postcode *</Label><Input value={f.postcode} onChange={e => set("postcode", e.target.value)} className="mt-1" data-testid="client-property-postcode" /></div>
            <div><Label className="text-xs">Type</Label><Input value={f.property_type} onChange={e => set("property_type", e.target.value)} placeholder="Office / Residential / Retail" className="mt-1" data-testid="client-property-type" /></div>
            <div className="col-span-2"><Label className="text-xs">what3words</Label><Input value={f.what3words} onChange={e => set("what3words", e.target.value)} placeholder="///filled.count.soap" className="mt-1 font-mono" data-testid="client-property-w3w" /></div>
            <div><Label className="text-xs">Site contact</Label><Input value={f.site_contact_name} onChange={e => set("site_contact_name", e.target.value)} className="mt-1" data-testid="client-property-contact-name" /></div>
            <div><Label className="text-xs">Contact phone</Label><Input value={f.site_contact_phone} onChange={e => set("site_contact_phone", e.target.value)} className="mt-1" data-testid="client-property-contact-phone" /></div>
            <div className="col-span-2"><Label className="text-xs">Contact email</Label><Input type="email" value={f.site_contact_email} onChange={e => set("site_contact_email", e.target.value)} className="mt-1" data-testid="client-property-contact-email" /></div>
            <div className="col-span-2"><Label className="text-xs">Access notes</Label>
              <textarea value={f.access_notes} onChange={e => set("access_notes", e.target.value)} rows={3} placeholder="Alarm codes, key safe, parking, sign-in…"
                className="mt-1 w-full text-sm rounded-md border border-border bg-background px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary" data-testid="client-property-access" />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t border-border">
            <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
            <Button size="sm" onClick={save} disabled={saving} data-testid="client-property-save">{saving ? "Saving…" : property?.id ? "Save changes" : "Add property"}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
